import { dateComparator } from '@web-app/utils/sortComparators.utils';
import { trpcClient } from '@web-app/utils/trpc';
import { molecule } from 'bunshi';
import { atom } from 'jotai';
import { atomWithQuery } from 'jotai-tanstack-query';

import { sessionMolecule } from './sessionMolecule';

// ============================================================================
// Lab Documents Molecule
// ============================================================================

/**
 * Lab Documents Molecule
 *
 * Provides all lab document atoms scoped to a session (keyed by roomId).
 * Depends on SessionMolecule for patientIdAtom.
 *
 * Includes atoms for:
 * - Lab documents list fetching and sorting
 * - Current document selection
 * - Presigned download URL management
 */
export const labDocumentsMolecule = molecule((mol) => {
  const { patientIdAtom } = mol(sessionMolecule);
  const showAllDocumentsAtom = atom(false);
  // Cache key only. Undefined until LabDocumentsTab copies Clerk's user id
  // after isLoaded, so the list cannot be stored under a shared empty viewer.
  const viewerIdAtom = atom<string | undefined>(undefined);

  // ============================================================================
  // LAB DOCUMENTS QUERY ATOM
  // ============================================================================

  /**
   * Atom for fetching lab documents for the current patient via tRPC.
   * Depends on patientIdAtom from SessionMolecule.
   */
  const labDocumentsQueryAtom = atomWithQuery((get) => {
    const patientId = get(patientIdAtom);
    const includeAllDocuments = get(showAllDocumentsAtom);
    const viewerId = get(viewerIdAtom);

    return {
      queryKey: [
        'labDocuments',
        'byPatientId',
        patientId,
        includeAllDocuments,
        viewerId,
      ],

      queryFn: async ({ queryKey }) => {
        const [
          ,
          ,
          queryPatientId,
          queryIncludeAllDocuments,
          queryViewerId,
        ] = queryKey as [
          string,
          string,
          string | undefined,
          boolean,
          string | undefined,
        ];

        if (!queryPatientId || queryViewerId === undefined) {
          return { documents: [] };
        }

        const result = await trpcClient.labDocuments.getByPatientId.query({
          patientId: queryPatientId,
          includeAllDocuments: queryIncludeAllDocuments,
          viewerId: queryViewerId,
        });

        return result;
      },

      enabled: !!patientId && viewerId !== undefined,
      staleTime: 5 * 60 * 1000,
      retry: 3,
      retryDelay: 10 * 1000,
      refetchOnWindowFocus: false,
    };
  });

  // ============================================================================
  // DERIVED ATOMS - Lab Documents List
  // ============================================================================

  /**
   * Derived atom that extracts the documents array from query result,
   * sorted by createdAt descending (most recent first).
   */
  const labDocumentsAtom = atom((get) => {
    const queryResult = get(labDocumentsQueryAtom);
    const documents = queryResult.data?.documents || [];

    return [...documents].sort(dateComparator('DESC', 'createdAt'));
  });

  /**
   * Derived atom for loading state of lab documents query.
   */
  const labDocumentsIsLoadingAtom = atom((get) => {
    const queryResult = get(labDocumentsQueryAtom);
    return queryResult.status === 'pending';
  });

  /**
   * Derived atom for error state of lab documents query.
   */
  const labDocumentsErrorAtom = atom((get) => {
    const queryResult = get(labDocumentsQueryAtom);
    return queryResult.error;
  });

  // ============================================================================
  // CURRENT LAB DOCUMENT SELECTION ATOMS
  // ============================================================================

  /**
   * Writable atom storing the currently selected lab document ID.
   */
  const currentLabDocumentIdAtom = atom<string | null>(null);

  /**
   * Derived atom that finds and returns the currently selected lab document.
   */
  const currentLabDocumentAtom = atom((get) => {
    const documentId = get(currentLabDocumentIdAtom);
    const documents = get(labDocumentsAtom);

    if (!documentId) {
      return undefined;
    }

    return documents.find((doc) => doc.id === documentId);
  });

  // ============================================================================
  // LAB DOCUMENT DOWNLOAD URL QUERY ATOM
  // ============================================================================

  /**
   * Atom for fetching a presigned S3 download URL for the current lab document.
   * Uses a shorter staleTime (4 min) since presigned URLs expire after 5 minutes.
   * gcTime is 0 to prevent caching of expired presigned URLs.
   */
  const labDocumentDownloadUrlQueryAtom = atomWithQuery((get) => {
    const documentId = get(currentLabDocumentIdAtom);
    const patientId = get(patientIdAtom);

    return {
      queryKey: ['labDocuments', 'downloadUrl', documentId, patientId],

      queryFn: async ({ queryKey }) => {
        const [, , queryDocumentId, queryPatientId] = queryKey as [
          string,
          string,
          string | null,
          string | undefined,
        ];

        if (!queryDocumentId || !queryPatientId) {
          return { downloadUrl: '', expiresAt: '' };
        }

        const result = await trpcClient.labDocuments.getDownloadUrl.query({
          documentId: queryDocumentId,
          patientId: queryPatientId,
        });

        return result;
      },

      enabled: !!documentId && !!patientId,
      staleTime: 4 * 60 * 1000,
      gcTime: 0,
      retry: 2,
      refetchOnWindowFocus: false,
    };
  });

  // ============================================================================
  // DERIVED ATOMS - Download URL
  // ============================================================================

  /**
   * Derived atom that extracts the presigned download URL string.
   */
  const labDocumentDownloadUrlAtom = atom((get) => {
    const queryResult = get(labDocumentDownloadUrlQueryAtom);
    return queryResult.data?.downloadUrl;
  });

  /**
   * Derived atom for loading state of the download URL query.
   */
  const labDocumentDownloadUrlIsLoadingAtom = atom((get) => {
    const queryResult = get(labDocumentDownloadUrlQueryAtom);
    return queryResult.status === 'pending';
  });

  /**
   * Derived atom that extracts the expiresAt timestamp string.
   */
  const labDocumentDownloadUrlExpiresAtAtom = atom((get) => {
    const queryResult = get(labDocumentDownloadUrlQueryAtom);
    return queryResult.data?.expiresAt;
  });

  /**
   * Derived atom that exposes the refetch function from the download URL query.
   */
  const refetchLabDocumentDownloadUrlAtom = atom((get) => {
    const queryResult = get(labDocumentDownloadUrlQueryAtom);
    return queryResult.refetch;
  });

  return {
    showAllDocumentsAtom,
    viewerIdAtom,
    labDocumentsQueryAtom,
    labDocumentsAtom,
    labDocumentsIsLoadingAtom,
    labDocumentsErrorAtom,
    currentLabDocumentIdAtom,
    currentLabDocumentAtom,
    labDocumentDownloadUrlQueryAtom,
    labDocumentDownloadUrlAtom,
    labDocumentDownloadUrlIsLoadingAtom,
    labDocumentDownloadUrlExpiresAtAtom,
    refetchLabDocumentDownloadUrlAtom,
  };
});
